import { useEffect, useState } from "react";
import { useMutation } from "convex/react";
import { anyApi } from "convex/server";
import { useNavigate } from "react-router-dom";
import { Dialog } from "./Dialog";
import { Field, inputCls } from "./form-controls";

// Slug rules mirror convex/properties.ts. Live-validated so the user
// gets feedback before submit.
const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,30}[a-z0-9])?$/;

export function AddPropertyDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const create = useMutation(anyApi.properties.create);
  const navigate = useNavigate();
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [timezone, setTimezone] = useState("America/Los_Angeles");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSlug("");
      setName("");
      setAddress("");
      setTimezone("America/Los_Angeles");
      setError(null);
    }
  }, [open]);

  const slugValid = SLUG_RE.test(slug);
  const canSubmit =
    slugValid && name.trim() && address.trim() && timezone.trim() && !busy;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const { slug: createdSlug } = (await create({
        slug,
        name,
        address,
        timezone,
      })) as { slug: string };
      onClose();
      navigate(`/${createdSlug}/dashboard`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Add property" width="md">
      <form onSubmit={onSubmit} className="px-6 py-5 space-y-4">
        <Field label="Slug" required>
          <input
            className={inputCls}
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            placeholder="sugarshack"
            autoFocus
          />
          <p className="mt-1 text-xs text-slate-500">
            Used in the URL: <span className="font-mono">/{slug || "…"}/dashboard</span>.
            Lowercase letters, numbers, and dashes only. Can't be changed later.
          </p>
          {slug && !slugValid && (
            <p className="mt-1 text-xs text-red-600">
              2-32 chars, lowercase letters/numbers, with optional internal dashes.
            </p>
          )}
        </Field>

        <Field label="Name" required>
          <input
            className={inputCls}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Sugar Shack"
          />
        </Field>

        <Field label="Address" required>
          <input
            className={inputCls}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="123 Some Street, City, State 00000"
          />
        </Field>

        <Field label="Timezone" required>
          <input
            className={inputCls}
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            placeholder="America/Los_Angeles"
          />
          <p className="mt-1 text-xs text-slate-500">
            IANA timezone. Affects how schedule times and rent periods are
            rendered.
          </p>
        </Field>

        {error && <p className="text-xs text-red-600">{error}</p>}

        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            type="submit"
            disabled={!canSubmit}
            className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 transition-colors disabled:opacity-50"
          >
            {busy ? "Creating…" : "Create property"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
